/**
 * ScheduledChangesSection Component
 * Shows pending budget changes, scheduled pauses, and automation rules
 */

import React, { useState, useEffect } from 'react';
import {
    Calendar,
    DollarSign,
    Pause,
    Play,
    TrendingUp,
    AlertCircle,
    Clock,
    Plus,
    Trash2,
    Edit2
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { formatCurrency } from '../../utils/objectiveHelpers';
import { formatDate } from '../../utils/helpers';

const API_BASE = 'http://localhost:3001';

const ScheduledChangesSection = ({ campaign }) => {
    const { currentUser } = useAuth();
    const { selectedBrand } = useApp();
    const [scheduledChanges, setScheduledChanges] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchScheduledChanges = async () => {
            // Use platformCampaignId for Meta API calls (not Firestore document ID)
            const platformId = campaign?.platformCampaignId || campaign?.id;
            if (!platformId || !currentUser || !selectedBrand) {
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                const params = new URLSearchParams({
                    userId: currentUser.uid,
                    brandId: selectedBrand.id,
                    campaignId: platformId,
                    platform: campaign.platform || 'meta'
                });

                const response = await fetch(`${API_BASE}/api/campaigns/${platformId}/scheduled-changes?${params}`);

                if (response.ok) {
                    const data = await response.json();
                    setScheduledChanges(data.changes || []);
                } else if (response.status === 404) {
                    // Endpoint not implemented yet - show empty state instead of error
                    setScheduledChanges([]);
                } else {
                    const errorData = await response.json().catch(() => ({}));
                    setError(errorData.error || `Failed to fetch scheduled changes (${response.status})`);
                }
            } catch (err) {
                console.error('Error fetching scheduled changes:', err);
                setError('Failed to connect to server. Please check your connection.');
            } finally {
                setLoading(false);
            }
        };

        fetchScheduledChanges();
    }, [campaign?.id, currentUser, selectedBrand]);

    const handleDeleteChange = async (changeId) => {
        // TODO: Implement delete scheduled change
        setScheduledChanges(prev => prev.filter(c => c.id !== changeId));
    };

    if (loading) {
        return (
            <div style={{ paddingTop: 'var(--space-3)' }}>
                {[1, 2].map(i => (
                    <div
                        key={i}
                        style={{
                            height: '60px',
                            background: 'var(--color-background)',
                            borderRadius: 'var(--radius-md)',
                            marginBottom: 'var(--space-2)',
                            animation: 'pulse 2s infinite'
                        }}
                    />
                ))}
            </div>
        );
    }

    if (error) {
        return (
            <div style={{
                padding: 'var(--space-4)',
                textAlign: 'center',
                color: 'var(--color-text-tertiary)'
            }}>
                <AlertCircle size={24} style={{ marginBottom: 'var(--space-2)', color: 'var(--color-error)' }} />
                <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-error)' }}>{error}</p>
            </div>
        );
    }

    return (
        <div style={{ paddingTop: 'var(--space-3)' }}>
            {/* Add New Button */}
            <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginBottom: 'var(--space-3)'
            }}>
                <button
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-1)',
                        padding: 'var(--space-2) var(--space-3)',
                        background: 'var(--color-primary-alpha)',
                        border: '1px solid var(--color-primary)',
                        borderRadius: 'var(--radius-md)',
                        color: 'var(--color-primary)',
                        fontSize: 'var(--font-size-sm)',
                        cursor: 'pointer'
                    }}
                >
                    <Plus size={14} />
                    Schedule Change
                </button>
            </div>

            {scheduledChanges.length === 0 ? (
                <div style={{
                    padding: 'var(--space-4)',
                    textAlign: 'center',
                    color: 'var(--color-text-tertiary)',
                    background: 'var(--color-background)',
                    borderRadius: 'var(--radius-md)'
                }}>
                    <Calendar size={24} style={{ marginBottom: 'var(--space-2)', opacity: 0.5 }} />
                    <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
                        No scheduled changes for this campaign
                    </p>
                    <p style={{ margin: 'var(--space-1) 0 0 0', fontSize: 'var(--font-size-xs)' }}>
                        Schedule budget changes, pauses, or automations
                    </p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                    {scheduledChanges
                        .sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate))
                        .map((change, index) => (
                            <ScheduledChangeCard
                                key={change.id || index}
                                change={change}
                                onDelete={() => handleDeleteChange(change.id)}
                            />
                        ))}
                </div>
            )}

            {/* Automation Rules Section */}
            <div style={{ marginTop: 'var(--space-6)' }}>
                <h4 style={{
                    margin: '0 0 var(--space-3) 0',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 600,
                    color: 'var(--color-text-secondary)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)'
                }}>
                    <TrendingUp size={14} />
                    Active Automation Rules
                </h4>

                <div style={{
                    padding: 'var(--space-3)',
                    background: 'var(--color-background)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px dashed var(--color-border)'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <span style={{
                            fontSize: 'var(--font-size-sm)',
                            color: 'var(--color-text-tertiary)'
                        }}>
                            No automation rules configured
                        </span>
                        <button
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-1)',
                                padding: 'var(--space-1) var(--space-2)',
                                background: 'transparent',
                                border: '1px solid var(--color-border)',
                                borderRadius: 'var(--radius-md)',
                                color: 'var(--color-text-secondary)',
                                fontSize: 'var(--font-size-xs)',
                                cursor: 'pointer'
                            }}
                        >
                            <Plus size={12} />
                            Add Rule
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Scheduled Change Card Component
const ScheduledChangeCard = ({ change, onDelete }) => {
    const getChangeIcon = (type) => {
        switch (type) {
            case 'budget_increase':
                return { icon: TrendingUp, color: 'var(--color-success)' };
            case 'budget_decrease':
                return { icon: DollarSign, color: 'var(--color-warning)' };
            case 'pause':
                return { icon: Pause, color: 'var(--color-error)' };
            case 'resume':
                return { icon: Play, color: 'var(--color-success)' };
            default:
                return { icon: Calendar, color: 'var(--color-primary)' };
        }
    };

    const getChangeDescription = (change) => {
        switch (change.type) {
            case 'budget_increase':
                return `Increase budget by ${change.amount ? formatCurrency(change.amount) : `${change.percentage}%`}`;
            case 'budget_decrease':
                return `Decrease budget by ${change.amount ? formatCurrency(change.amount) : `${change.percentage}%`}`;
            case 'pause':
                return 'Pause campaign';
            case 'resume':
                return 'Resume campaign';
            default:
                return change.description || 'Scheduled change';
        }
    };

    const { icon: Icon, color } = getChangeIcon(change.type);
    const scheduledDate = new Date(change.scheduledDate);
    const isUpcoming = scheduledDate > new Date();
    const daysUntil = Math.ceil((scheduledDate - new Date()) / (1000 * 60 * 60 * 24));

    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            padding: 'var(--space-3)',
            background: 'var(--color-background)',
            borderRadius: 'var(--radius-md)',
            border: `1px solid ${isUpcoming ? color + '40' : 'var(--color-border)'}`
        }}>
            {/* Icon */}
            <div style={{
                width: '36px',
                height: '36px',
                borderRadius: 'var(--radius-md)',
                background: `${color}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color
            }}>
                <Icon size={18} />
            </div>

            {/* Details */}
            <div style={{ flex: 1 }}>
                <div style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 500,
                    color: 'var(--color-text-primary)',
                    marginBottom: '2px'
                }}>
                    {getChangeDescription(change)}
                </div>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-tertiary)'
                }}>
                    <Clock size={12} />
                    <span>
                        {formatDate(scheduledDate, 'short')}
                        {isUpcoming && daysUntil <= 7 && (
                            <span style={{
                                marginLeft: 'var(--space-1)',
                                color: daysUntil <= 1 ? 'var(--color-warning)' : 'inherit'
                            }}>
                                ({daysUntil === 0 ? 'Today' : daysUntil === 1 ? 'Tomorrow' : `In ${daysUntil} days`})
                            </span>
                        )}
                    </span>
                </div>
            </div>

            {/* Status Badge */}
            <span style={{
                padding: '2px 8px',
                borderRadius: 'var(--radius-full)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 500,
                background: isUpcoming ? `${color}15` : 'var(--color-surface-hover)',
                color: isUpcoming ? color : 'var(--color-text-tertiary)'
            }}>
                {isUpcoming ? 'Pending' : 'Completed'}
            </span>

            {/* Actions */}
            {isUpcoming && (
                <div style={{
                    display: 'flex',
                    gap: 'var(--space-1)'
                }}>
                    <button
                        style={{
                            padding: 'var(--space-1)',
                            background: 'transparent',
                            border: 'none',
                            color: 'var(--color-text-tertiary)',
                            cursor: 'pointer',
                            borderRadius: 'var(--radius-sm)'
                        }}
                        title="Edit"
                        onMouseEnter={(e) => e.target.style.color = 'var(--color-primary)'}
                        onMouseLeave={(e) => e.target.style.color = 'var(--color-text-tertiary)'}
                    >
                        <Edit2 size={14} />
                    </button>
                    <button
                        onClick={onDelete}
                        style={{
                            padding: 'var(--space-1)',
                            background: 'transparent',
                            border: 'none',
                            color: 'var(--color-text-tertiary)',
                            cursor: 'pointer',
                            borderRadius: 'var(--radius-sm)'
                        }}
                        title="Delete"
                        onMouseEnter={(e) => e.target.style.color = 'var(--color-error)'}
                        onMouseLeave={(e) => e.target.style.color = 'var(--color-text-tertiary)'}
                    >
                        <Trash2 size={14} />
                    </button>
                </div>
            )}
        </div>
    );
};

export default ScheduledChangesSection;
